<template>
  <div class="flex flex-col">
    <div class="mb-4 flex items-center justify-between">
      <h2 class="text-lg font-medium text-background-fg/87">
        Platform Summary
      </h2>
      <div
        v-if="
          props.source !== 'beepaidgo' && data !== undefined
        "
        class="flex items-center"
      >
        <label class="mr-2 text-xs text-background-fg/87"
          >Currency:</label
        >
        <InputSelect
          v-model="selectedCurrency"
          :options="currencyOptions"
        />
      </div>
    </div>

    <QueryError
      v-if="isError"
      class="min-h-72"
      :show-retry="true"
      @retry="refetch"
    >
      Failed to retrieve platform summary
    </QueryError>
    <div
      v-else
      class="grid grid-cols-1 gap-2 md:grid-cols-3"
    >
      <BaseCard>
        <PlatformSummaryCard
          title="Card Turnover"
          :current-values="
            getStatValues('cardRevenue').current
          "
          :previous-values="
            getStatValues('cardRevenue').previous
          "
          :loading="isLoading"
          type="currency"
          :currency="selectedCurrency"
        />
      </BaseCard>
      <BaseCard>
        <PlatformSummaryCard
          title="Clients Transacting"
          :current-values="
            getStatValues('clientsTransacting').current
          "
          :previous-values="
            getStatValues('clientsTransacting').previous
          "
          :loading="isLoading"
        />
      </BaseCard>
      <BaseCard>
        <PlatformSummaryCard
          title="New Clients"
          :current-values="
            getStatValues('newClients').current
          "
          :previous-values="
            getStatValues('newClients').previous
          "
          :loading="isLoading"
        />
      </BaseCard>
    </div>
    <div
      v-if="!!lastGenerated"
      class="mr-2 mt-2 self-end text-xs text-black/30"
    >
      last generated {{ lastGenerated }} ago
    </div>
  </div>
</template>

<script setup lang="ts">
import * as DateFns from "date-fns"
import type { MerchantCurrencyCode } from "@/utils/currency"

import {
  useTpplusPlatformSummary,
  type CurrencyStats,
} from "@/services/reports/retrievePlatformSummary"

const props = defineProps<{
  source: PlatformStatsSources
}>()

type StatKey = keyof CurrencyStats

type TransformedStatValues = {
  current: {
    day: number | undefined
    week: number | undefined
    month: number | undefined
  }
  previous: {
    day: number | undefined
    week: number | undefined
    month: number | undefined
  }
}

const { data, isLoading, isError, refetch } =
  props.source === "tpplus"
    ? useTpplusPlatformSummary()
    : useBeepaidGoPlatformSummary()

const { defaultMerchantCurrency } = useOrganizationConfig()
const selectedCurrencyOverride =
  ref<MerchantCurrencyCode | null>(null)
const selectedCurrency = computed<MerchantCurrencyCode>({
  get: () =>
    selectedCurrencyOverride.value ??
    defaultMerchantCurrency.value,
  set: (newValue) => {
    selectedCurrencyOverride.value = newValue
  },
})

const currencyOptions = merchantCurrencyCodeOptions

const getStatValues = (
  statKey: StatKey
): TransformedStatValues => {
  const stats =
    data.value?.data?.[selectedCurrency.value]?.[statKey]
  if (!stats) {
    return {
      current: {
        day: undefined,
        week: undefined,
        month: undefined,
      },
      previous: {
        day: undefined,
        week: undefined,
        month: undefined,
      },
    }
  }

  return {
    current: {
      day: stats.today,
      week: stats.thisWeek,
      month: stats.thisMonth,
    },
    previous: {
      day: stats.thisTimeLastWeek,
      week: stats.lastWeek,
      month: stats.lastMonth,
    },
  }
}

const lastGenerated = computed(
  () =>
    data.value &&
    DateFns.formatDistance(
      new Date(),
      data.value.generatedAt
    )
)
</script>

<style scoped lang="postcss">
.stat-grid {
  @apply grid grid-flow-col grid-cols-3 gap-x-5 gap-y-2;
  grid-template-rows: repeat(4, auto);
  grid-template-columns: 1fr auto 1fr auto 1fr;
}

.column-divider {
  @apply row-span-4 -my-5 w-px bg-gray-200;
}
</style>
